
import React, { useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send } from "lucide-react";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { useIsMobile } from "@/hooks/use-mobile";

interface MessageInputProps {
  newMessage: string;
  setNewMessage: (message: string) => void;
  handleSendMessage: () => void;
  isSending: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({
  newMessage,
  setNewMessage,
  handleSendMessage,
  isSending
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useIsMobile();
  
  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
  };

  // Handle enter key for sending
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (newMessage.trim() && !isSending) {
        handleSendMessage();
      }
    }
  };

  // Handle send button click
  const handleSend = () => {
    if (newMessage.trim() && !isSending) {
      handleSendMessage();
    }
  };

  const isEmpty = newMessage.trim().length === 0;
  
  // Focus the textarea when component mounts
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Auto-resize the textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    
    // Set to scrollHeight, but clamp between min and max height
    const maxHeight = isMobile ? 120 : 150;
    const newHeight = Math.min(Math.max(textarea.scrollHeight, 56), maxHeight);
    textarea.style.height = `${newHeight}px`;
  }, [newMessage, isMobile]);

  // Set different placeholder text based on device
  const placeholderText = isMobile 
    ? "Type message..." 
    : "Type your message... (Enter to send, Shift+Enter for new line)";

  return (
    <div className="p-4 border-t bg-background/90 backdrop-blur-sm z-10">
      <div className="flex items-end max-w-4xl mx-auto">
        <Textarea
          ref={textareaRef}
          value={newMessage}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholderText}
          className="flex-1 resize-none focus-visible:ring-primary min-h-[56px]"
          disabled={isSending}
          maxLength={5000}
        />
        <Button 
          onClick={handleSend}
          className="ml-2 self-end h-10 w-10 p-0"
          disabled={isEmpty || isSending}
          size="icon"
          variant="default"
        >
          {isSending ? <LoadingSpinner size="sm" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
      {!isMobile && (
        <div className="text-xs text-muted-foreground mt-1 max-w-4xl mx-auto">
          Press Enter to send, Shift+Enter for new line
        </div>
      )}
    </div>
  );
};

export default React.memo(MessageInput);
